import './FeatureFlagCard.scss';
import { createAsyncSelectors } from '@lib/selectors';
import { FeatureFlagLabel } from '@sonar-software/sonar-core';
import { objectKeys } from '@sonar-software/sonar-ts-lib';
import { ShardCatalogSearch } from '../../ShardCatalogSearch';
import { SonarCard } from '@ui/general';
import { SonarTable } from '@ui/SonarTable';
import { SonarText } from '@ui/text';
import { When } from 'react-if';
import { ADMIN_DASHBOARD_COPY, COPY_GENERIC, COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { adminActions } from '@sonar-software/sonar-admin';
import { Button, Checkbox, Flex, Popover, Radio, Select, Space, Tag } from 'antd';
import { CloseOutlined, PlusOutlined, UserOutlined } from '@ant-design/icons';
import React, { useCallback, useMemo } from 'react';
import { useAction, useAsyncProcessing, useBoolean } from '@hooks';
const overrideDefaultSelectors = createAsyncSelectors((state) => state.admin.featureFlag.byId.customer.override);
const setDefaultSelectors = createAsyncSelectors((state) => state.admin.featureFlag.byId.customer.useDefault);
const patchSelectors = createAsyncSelectors((state) => state.admin.featureFlag.byId.patch);
const lowercaseFeatureFlags = objectKeys(FeatureFlagLabel).reduce((obj, key) => {
    obj[key.toLocaleLowerCase()] = FeatureFlagLabel[key];
    return obj;
}, {});
function getRowKey(entry, flagName) {
    return `${flagName}-${entry.id}-${entry.shardName}`;
}
const stateOptions = [
    {
        label: COPY_GENERIC.FORCE_ENABLED,
        value: true
    },
    {
        label: COPY_GENERIC.UNSET,
        value: null
    },
    {
        label: COPY_GENERIC.FORCE_DISABLED,
        value: false
    }
];
export const FeatureFlagCard = ({ featureFlag, groupOptions }) => {
    var _a, _b, _c;
    const [isOpen, setIsOpen] = useBoolean(false);
    const resetShardCatalogDetails = useAction(adminActions.resetGetAdminSearchShardCatalogDetails);
    const overrideDefault = useAction(adminActions.requestPostOverrideAdminFeatureFlagByCustomerId);
    const resetGet = useAction(adminActions.requestGetAdminFeatureFlags);
    const patch = useAction(adminActions.requestPatchAdminFeatureFlagById);
    const setDefault = useAction(adminActions.requestPostUseDefaultAdminFeatureFlagByCustomerId);
    const exceptionCount = (_a = featureFlag === null || featureFlag === void 0 ? void 0 : featureFlag.exceptionCustomers.filter((customer) => customer.enabled !== (featureFlag === null || featureFlag === void 0 ? void 0 : featureFlag.enabledByDefault)).length) !== null && _a !== void 0 ? _a : 0;
    const overrideProcess = useAsyncProcessing({
        resetAction: resetGet,
        stateSelector: overrideDefaultSelectors.state
    });
    const setDefaultProcess = useAsyncProcessing({
        resetAction: resetGet,
        stateSelector: setDefaultSelectors.state
    });
    const patchProcess = useAsyncProcessing({
        resetAction: resetGet,
        stateSelector: patchSelectors.state
    });
    const handleAddCustomer = useCallback((details) => {
        if (!details) {
            return;
        }
        setIsOpen.off();
        if (featureFlag &&
            !featureFlag.exceptionCustomers.find((customer) => customer.id === details.customerId &&
                customer.shardName === details.shardName &&
                featureFlag.enabledByDefault !== customer.enabled)) {
            overrideProcess.watchNextCall();
            overrideDefault({
                customerId: details.customerId,
                customerName: details.customerName,
                enabled: !featureFlag.enabledByDefault,
                featureFlag: featureFlag.name,
                shardName: details.shardName
            });
        }
        resetShardCatalogDetails();
    }, [featureFlag, setIsOpen, overrideDefault, overrideProcess, resetShardCatalogDetails]);
    const handleSetDefault = useCallback((customer) => {
        if (featureFlag) {
            setDefaultProcess.watchNextCall();
            setDefault({
                customerId: customer.id,
                featureFlag: featureFlag.name,
                shardName: customer.shardName
            });
        }
    }, [featureFlag, setDefault, setDefaultProcess]);
    const handlePatch = useCallback((id, forcedValue, groupId, groupName, defaultValue) => {
        patchProcess.watchNextCall();
        patch({
            defaultValue,
            forcedValue,
            groupId,
            groupName,
            id
        });
    }, [patch, patchProcess]);
    const customerTableColumns = useMemo(() => [
        {
            dataIndex: 'name',
            key: 'name',
            render: (value, record) => React.createElement(Flex, { align: 'center', justify: 'space-between' },
                React.createElement(SonarText, null, record.name),
                React.createElement(Button, { onClick: () => handleSetDefault(record), type: 'text' },
                    React.createElement(CloseOutlined, null))),
            title: COPY_SPECIFIC.NAME
        }
    ], [handleSetDefault]);
    if (!featureFlag) {
        return null;
    }
    return (React.createElement(SonarCard, { className: 'feature-flag-card', extra: React.createElement(Radio.Group, { buttonStyle: 'solid', id: 'feature-flag-state', onChange: (e) => handlePatch(featureFlag.name, e.target.value), options: stateOptions, optionType: 'button', value: featureFlag.state }), title: React.createElement(Space, null, (_b = lowercaseFeatureFlags[featureFlag.name.toLowerCase()]) !== null && _b !== void 0 ? _b : featureFlag.name,
            React.createElement(When, { condition: featureFlag.enabledByDefault },
                React.createElement(Tag, { color: 'geekblue' }, COPY_SPECIFIC.ENABLE_BY_DEFAULT))) },
        React.createElement(Space, { direction: 'vertical', style: { width: '100%' } },
            React.createElement(When, { condition: featureFlag.state === null },
                React.createElement(Flex, { justify: 'space-between' },
                    React.createElement(Space, null,
                        React.createElement(UserOutlined, null),
                        React.createElement(SonarText, { weight: 'semibold' }, featureFlag.enabledByDefault ?
                            COPY_SPECIFIC.DISABLED_CUSTOMERS_COUNT(exceptionCount) :
                            COPY_SPECIFIC.ENABLED_CUSTOMERS_COUNT(exceptionCount))),
                    React.createElement(Popover, { content: React.createElement(ShardCatalogSearch, { filtertype: 'customer', id: 'feature-flag-customer-search', onDetailsChanged: handleAddCustomer, placeholder: ADMIN_DASHBOARD_COPY.SEARCH_CUSTOMERS_PLACEHOLDER }), destroyTooltipOnHide: true, onOpenChange: (value) => (value ? setIsOpen.on() : setIsOpen.off()), open: isOpen, placement: 'bottomRight', trigger: 'click' },
                        React.createElement(Button, { id: 'feature-flag-add-customer-button', type: 'primary' },
                            React.createElement(PlusOutlined, null),
                            COPY_SPECIFIC.ADD_CUSTOMER))),
                React.createElement(SonarTable, { columns: customerTableColumns, dataSource: featureFlag.exceptionCustomers
                        .filter((customer) => customer.enabled !== (featureFlag === null || featureFlag === void 0 ? void 0 : featureFlag.enabledByDefault)), id: 'exception-customers-table', pagination: false, rowKey: (e) => getRowKey(e, featureFlag.name), scroll: { x: 'max-content' } })),
            React.createElement(SonarText, { weight: 'semibold' }, COPY_SPECIFIC.CONFIG),
            React.createElement(SonarText, null, COPY_SPECIFIC.FEATURE_FLAG_GROUP),
            React.createElement(Select, { allowClear: true, className: 'feature-flag-group-select', id: 'feature-flag-group-select', onChange: (groupId) => {
                    var _a, _b;
                    return handlePatch(featureFlag.name, undefined, groupId, (_b = (_a = groupOptions === null || groupOptions === void 0 ? void 0 : groupOptions.find((x) => x.value === groupId)) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : null);
                }, onClear: () => handlePatch(featureFlag.name, undefined, null, null), options: groupOptions, placeholder: COPY_SPECIFIC.SELECT_GROUP, value: (_c = featureFlag.groupId) !== null && _c !== void 0 ? _c : undefined }),
            React.createElement(Checkbox, { checked: featureFlag.enabledByDefault, className: 'enable-by-default-checkbox', onChange: (e) => handlePatch(featureFlag.name, undefined, undefined, undefined, e.target.checked) }, COPY_SPECIFIC.ENABLE_BY_DEFAULT))));
};
